import { useEffect } from 'react'

import type { NextPage } from 'next'
import { useRouter } from 'next/router'

import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import AuthDialog from '~/components/blocks/AuthDialog/AuthDialog'
import useAuthFormAnalytics from '~/hooks/useAuthFormAnalytics'
import { addTranslations } from '~/utils/i18n'
import { createLangStaticPaths } from '~/utils/langPaths'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/auth.module.css'

// Only allow same-origin relative paths to prevent open-redirect attacks
const sanitiseRedirect = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined
  if (!raw.startsWith('/') || raw.startsWith('//')) return undefined
  return raw
}

const LoginPage: NextPage = () => {
  const { handleLoginPageLoaded } = useAuthFormAnalytics()
  const router = useRouter()
  const redirectTo = sanitiseRedirect(router.query.redirect)

  useEffect(() => {
    handleLoginPageLoaded()
  }, [])

  return (
    <Fixture>
      <div className={styles.base}>
        <Container>
          <AuthDialog defaultTab={0} onCompleteUrl={redirectTo} />
        </Container>
      </div>
    </Fixture>
  )
}

const getStaticPropsOriginal = async () => {
  return {
    props: {
      url: '/login',
      noAnimatedTheme: true,
      hasGoogleReviews: false,
      meta: {
        title: 'Login: MyPortfolio | MyArtBroker',
      },
    },
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal()
  if ('notFound' in result || 'redirect' in result) {
    return result // Return notFound or redirect directly
  }
  return { props: await addTranslations(result.props, context) }
})
export const getStaticPaths = async () => createLangStaticPaths()

export default LoginPage
